import { styled } from "@mui/material";
import { api, types } from "api";
import { InfoSnackBar } from "components/InfoSnackBar";
import { ProfileLayout } from "components/ProfileLayout";
import { isValidEmail } from "helpers/authValidation";
import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import {
	ERROR_ADD_CHILD_TO_PARENT,
	SUCCESS_ADD_CHILD_TO_PARENT,
} from "messages";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { getPath, routes } from "routes";
import { UpdateChildPageForm } from "./UpdateChildPageForm";

const PREFIX = "UpdateChildPage";

const classes = {
	root: `${PREFIX}-root`,
};

const Root = styled("div")(() => ({
	[`&.${classes.root}`]: {
		height: "100%",
	},
}));

export const UpdateChildPage = (): JSX.Element => {
	const { updateLastBreadcrumbs } = useBreadcrumbs();
	const [submit, setSubmit] = useState(false);
	const [err, setErr] = useState(false);
	const params = useParams<{ userId: types.UUID }>();

	const [user, setUser] = useState<types.User | null>(null);

	const [childEmail, setChildEmail] = useState("");
	const history = useHistory();

	// Get the user id
	useEffect(() => {
		const query = params.userId
			? api.getUserDetails(params.userId)
			: api.getMyUserData();
		query
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				setErr(false);
				setUser(res.data);

				if (params.userId) {
					updateLastBreadcrumbs([
						{
							name: `${res.data.firstName} ${res.data.lastName}`,
							params: { userId: params.userId },
						},
						undefined,
					]);
				}
			})
			.catch(() => {
				setErr(true);
			});
	}, [params.userId, updateLastBreadcrumbs]);

	// Update the password
	const onSubmit = (event: React.FormEvent): void => {
		event.preventDefault();

		if (!isValidEmail(childEmail)) {
			setErr(true);
			setSubmit(true);
			return;
		}

		if (user?.userId === undefined) {
			setErr(true);
			setSubmit(true);
			return;
		}

		api
			.addChildToParent(user.userId, { childEmail })
			.then((res) => {
				if (res.status === 200) {
					history.push(
						params.userId
							? getPath(routes._.people._.userProfile, {
									userId: params.userId,
							  })
							: routes._.people._.me.path,
					);
					return;
				}
				setErr(true);
				setSubmit(true);
			})
			.catch(() => {
				setErr(true);
				setSubmit(true);
			});
	};

	return (
		<Root className={classes.root}>
			<InfoSnackBar
				isOpen={submit}
				onClose={() => setSubmit(false)}
				message={err ? ERROR_ADD_CHILD_TO_PARENT : SUCCESS_ADD_CHILD_TO_PARENT}
				severity={err ? "error" : "success"}
			/>

			<ProfileLayout user={user}>
				<UpdateChildPageForm
					userIdParam={params.userId}
					childEmail={childEmail}
					setChildEmail={setChildEmail}
					onSubmit={onSubmit}
				/>
			</ProfileLayout>
		</Root>
	);
};
